/* ============================================================
   <kurtens-sound-proof> — démonstrateur audio A/B (mécanique Bose)
   - vidéo muette + 2 pistes <audio> en boucle, tournant librement en parallèle
     (pas de recalage image/son : inutile pour une ambiance, et source de saccades)
   - les 2 pistes démarrent ensemble => restent synchro entre elles pour le toggle
   - toggle = bascule de volume (1 <-> 0), instantané, sans re-seek
   - crossfade photo rideau ouvert/fermé via data-closed sur la scène
   ============================================================ */
class KurtensSoundProof extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.stage = this.querySelector(".ksp__stage");
    this.video = this.querySelector(".ksp__video");
    this.frame = this.querySelector(".ksp__frame");
    this.playBtn = this.querySelector(".ksp__play");
    this.switchInput = this.querySelector(".ksp__switch-input");
    this.audioOpen = this.querySelector('.ksp__audio[data-track="open"]');
    this.audioClosed = this.querySelector('.ksp__audio[data-track="closed"]');

    if (!this.stage || !this.video) return;

    this.closed = this.stage.dataset.closed === "true";
    this.playLabel = this.playBtn
      ? this.playBtn.getAttribute("aria-label") || "Tester le son"
      : "Tester le son";
    this.applyVolumes();

    // Clic sur le cadre = lecture/pause (les photos du rideau sont superposées
    // et intercepteraient un clic posé sur la vidéo).
    this.frame.addEventListener("click", () => this.togglePlay());
    // Le bouton lecture est sous le média (plus dans le cadre) : handler dédié.
    if (this.playBtn) {
      this.playBtn.addEventListener("click", () => this.togglePlay());
    }
    this.video.addEventListener("ended", () => this.setPlaying(false));

    // Toggle natif (checkbox) : le clavier et l'accessibilité sont gérés nativement.
    if (this.switchInput) {
      this.switchInput.addEventListener("change", () =>
        this.setMode(this.switchInput.checked),
      );
    }

    // pause quand la section sort de l'écran
    if ("IntersectionObserver" in window) {
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting && this.playing) this.pause();
          });
        },
        { threshold: 0.15 },
      );
      this._io.observe(this);
    }
  }

  get tracks() {
    return [this.audioOpen, this.audioClosed].filter(Boolean);
  }

  applyVolumes() {
    // piste active = volume plein, l'autre coupée. Bascule instantanée.
    if (this.audioOpen) this.audioOpen.volume = this.closed ? 0 : 1;
    if (this.audioClosed) this.audioClosed.volume = this.closed ? 1 : 0;
  }

  setPlaying(on) {
    this.playing = on;
    this.stage.dataset.playing = on ? "true" : "false";
    if (this.playBtn) {
      this.playBtn.classList.toggle("is-playing", on);
      this.playBtn.setAttribute("aria-label", on ? "Pause" : this.playLabel);
    }
  }

  togglePlay() {
    this.playing ? this.pause() : this.play();
  }

  play() {
    this.applyVolumes();
    const t = this.video.currentTime || 0;
    this.tracks.forEach((a) => {
      try {
        a.currentTime = t;
      } catch (e) {}
    });
    const p = this.video.play();
    this.tracks.forEach((a) => {
      const ap = a.play();
      if (ap && ap.catch) ap.catch(() => {});
    });
    if (p && p.catch) p.catch(() => {});
    this.setPlaying(true);
  }

  pause() {
    this.video.pause();
    this.tracks.forEach((a) => a.pause());
    this.setPlaying(false);
  }

  setMode(closed) {
    this.closed = closed;
    this.stage.dataset.closed = closed ? "true" : "false";
    if (this.switchInput) this.switchInput.checked = closed;
    this.applyVolumes(); // bascule de volume instantanée, pas de re-seek
  }
}

if (!customElements.get("kurtens-sound-proof")) {
  customElements.define("kurtens-sound-proof", KurtensSoundProof);
}
